import {
  Alert,
  CustomCheckinInput,
  Translate,
} from '@innovatrics/checkin-components'
import { Grid, InputLabel } from '@mui/material'
import { DatePicker } from '@mui/lab'
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form'

interface DocumentReviewInputProps {
  name: string
  setDate?: React.Dispatch<React.SetStateAction<Date>>
  date?: Date
  multiline?: boolean
  minDate?: Date
  maxDate?: Date
  errorMessage?: boolean
  asterisk?: boolean
}

export const DocumentReviewInput = ({
  name,
  date,
  setDate,
  multiline,
  minDate,
  maxDate,
  errorMessage,
  asterisk,
}: DocumentReviewInputProps) => {
  const handleChange = (
    date: Date,
    field: ControllerRenderProps<FieldValues, string>
  ) => {
    if (
      date &&
      !isNaN(+date) &&
      date.toISOString() !== '1970-01-01T00:00:00.000Z'
    ) {
      field.onChange(new Date(+date - new Date().getTimezoneOffset() * 60000))
      setDate(date)
    } else {
      field.onChange(null)
      setDate(null)
    }
  }

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext()

  return (
    <Grid item xs={6}>
      <InputLabel>
        <Grid
          container
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: errors?.[name] ? 'error.main' : 'text.disabled',
          }}
        >
          <Grid item>
            <Translate
              entityName={`forms.${name}.tooltip`}
              defaultMessage={'Tooltip'}
            />
            {asterisk && <span style={{ color: '#E5352B' }}> *</span>}
          </Grid>
          <Grid item container alignItems={'center'} width="unset">
            {errors?.[name] && (
              <>
                <Translate
                  entityName={`forms.error`}
                  defaultMessage={'Warning'}
                />
                <Alert />
              </>
            )}
          </Grid>
        </Grid>
      </InputLabel>

      {setDate ? (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <DatePicker
              mask={'__/__/____'}
              minDate={minDate}
              maxDate={maxDate}
              value={date}
              onChange={(date) => handleChange(date, field)}
              renderInput={(params) => (
                <CustomCheckinInput
                  autoComplete="off"
                  fullWidth
                  variant="outlined"
                  sx={{
                    marginY: '5px',
                    borderRadius: '8px',
                    border: errors?.[`${name}`]
                      ? '1px solid #E5352B'
                      : '1px solid rgba(255, 255, 255, 0)',
                  }}
                  {...params}
                />
              )}
            />
          )}
        />
      ) : (
        <CustomCheckinInput
          fullWidth
          autoComplete="one-time-code"
          multiline={multiline}
          {...register(name)}
          variant="outlined"
          sx={{
            marginY: '5px',
            borderRadius: '8px',
            border: errors?.[name]
              ? '1px solid #E5352B'
              : '1px solid rgba(255, 255, 255, 0)',
          }}
        />
      )}
      {errorMessage && (
        <InputLabel
          sx={{
            fontSize: 12,
            color: '#E5352B',
            whiteSpace: 'normal',
          }}
        >
          {errors?.[name] ? (
            <Translate
              entityName={`forms.${name}.error`}
              defaultMessage={`Invalid input.`}
            />
          ) : (
            ' '
          )}
        </InputLabel>
      )}
    </Grid>
  )
}
